import { useState } from 'react'
import type {
  AvaliacaoSindromeCinto,
  ItemXABCDE,
  MetricasDeltaV,
  PulsoAceleracao,
} from '../../dominio/didatico/metricasCinematica'
import { MiniGraficoForcaG } from './MiniGraficoForcaG'

interface Props {
  metricasDeltaV: MetricasDeltaV
  pulsoAceleracao: PulsoAceleracao
  tempoNormalizado: number
  comCinto: boolean
  sindromeCinto: AvaliacaoSindromeCinto
  itensXabcde: readonly ItemXABCDE[]
}

/**
 * Painel de métricas físicas estimadas (ΔV, pulso ilustrativo em g) e correlação clínica XABCDE (Fase 1).
 */
export function PainelMetricasXABCDE({
  metricasDeltaV,
  pulsoAceleracao,
  tempoNormalizado,
  comCinto,
  sindromeCinto,
  itensXabcde,
}: Props) {
  const [letraFoco, setLetraFoco] = useState<'X' | 'A' | 'B' | 'C' | 'D' | 'E' | null>(null)

  const tomClassificacao =
    metricasDeltaV.classificacao === 'grave'
      ? 'perigo'
      : metricasDeltaV.classificacao === 'moderado'
        ? 'alerta'
        : 'normal'

  return (
    <div className="secao-metricas-xabcde">
      {/* 1. Régua Didática de Delta-V */}
      <div className="bloco-delta-v">
        <div className="cabecalho-bloco-metrica">
          <span className="titulo-metrica">Variação de Velocidade (ΔV estimado)</span>
          <span className={`badge-classificacao badge-${tomClassificacao}`}>
            {metricasDeltaV.rotuloClassificacao}
          </span>
        </div>

        <div className="valor-destaque-delta-v">
          <div className="numero-delta-v">
            <strong>{metricasDeltaV.deltaVKmh}</strong>
            <span>km/h</span>
          </div>
          <div className="detalhes-delta-v">
            <span className="sub-valor">{metricasDeltaV.deltaVMs} m/s líquidos</span>
            <span className="escala-ais">{metricasDeltaV.escalaAis}</span>
          </div>
        </div>

        {/* Barra visual de gravidade */}
        <div className="barra-escala-delta-v">
          <div
            className={`preenchimento-escala preenchimento-${tomClassificacao}`}
            style={{ width: `${Math.min(100, Math.max(10, (metricasDeltaV.deltaVKmh / 70) * 100))}%` }}
          />
        </div>

        <p className="descricao-delta-v">{metricasDeltaV.descricaoClinica}</p>
        <small className="dica">
          Estimativa didática por fatores fixos de tipo de impacto e alvo, não calculada a partir
          das velocidades da cena. Classes e textos clínicos orientam a discussão; não são diagnóstico.
        </small>

        {metricasDeltaV.criterioTransportePhtls && (
          <div className="alerta-transporte-phtls">
            <strong>Mecanismo de alta energia (ΔV estimado ≥ 35 km/h):</strong> discuta o mecanismo junto
            com os achados da vítima, conforme o protocolo de triagem adotado pelo curso. Este valor
            estimado, sozinho, não define destino nem prioridade.
          </div>
        )}
      </div>

      {/* 2. Mini-Gráfico do Pulso G(t) */}
      <MiniGraficoForcaG
        pulso={pulsoAceleracao}
        tempoNormalizado={tempoNormalizado}
        comCinto={comCinto}
      />

      {/* 3. Síndrome do Cinto & Submarining se aplicável */}
      {(sindromeCinto.temSubmarining || sindromeCinto.temSinalDoCinto) && (
        <div className={`alerta-sindrome-cinto ${sindromeCinto.temSubmarining ? 'alerta-submarining' : 'alerta-sinal-cinto'}`}>
          <strong>{sindromeCinto.temSubmarining ? 'Efeito Submarino Detectado' : 'Sinal do Cinto (Seatbelt Sign)'}</strong>
          <p>{sindromeCinto.descricao}</p>
          <ul className="lista-lesoes-cinto">
            {sindromeCinto.lesoesAssociadas.map((lesao, i) => (
              <li key={i}>{lesao}</li>
            ))}
          </ul>
          <small>{sindromeCinto.alerta}</small>
        </div>
      )}

      {/* 4. Mnemônico XABCDE Integrado */}
      <div className="bloco-xabcde">
        <span className="titulo-controle">Protocolo Primário XABCDE da Cinemática</span>
        <div className="grade-botoes-xabcde">
          {itensXabcde.map((item) => (
            <button
              key={item.letra}
              type="button"
              className={`btn-letra-xabcde btn-${item.prioridade} ${letraFoco === item.letra ? 'ativo' : ''}`}
              onClick={() => setLetraFoco(letraFoco === item.letra ? null : item.letra)}
            >
              <span className="letra-sigla">{item.letra}</span>
              <span className="rotulo-curto-xabcde">{item.nome.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Card do item XABCDE em foco ou visão geral resumida */}
        {letraFoco ? (
          (() => {
            const item = itensXabcde.find((i) => i.letra === letraFoco)!
            return (
              <div className={`detalhe-item-xabcde borda-${item.prioridade}`}>
                <div className="cabecalho-detalhe-x">
                  <strong>{item.letra} — {item.nome}</strong>
                  <span className={`tag-prioridade tag-${item.prioridade}`}>
                    {item.prioridade.toUpperCase()}
                  </span>
                </div>
                <p><strong>Achado Cinemático:</strong> {item.achadoCinematico}</p>
                <p><strong>Suspeita Clínica:</strong> {item.suspeitaClinica}</p>
                <p className="conduta-destaque"><strong>Conduta CBMMG:</strong> {item.condutaCbmmg}</p>
              </div>
            )
          })()
        ) : (
          <p className="dica">
            Selecione uma das letras do protocolo XABCDE acima para ver a correlação direta
            entre as marcas físicas da colisão e a conduta de exame primário.
          </p>
        )}
      </div>
    </div>
  )
}
